import { useCallback, useEffect, useState } from 'react';
import { Building2, ArrowRight } from 'lucide-react';
import { ukDate } from './invoiceUtils';

export const COMPANY_NAME = { LD: 'LUX Developments', LC: 'LUX Construction' };

/** Still-to-invoice schedule days per LUX company for a period. Rule: one invoice per company. */
export function useCompanySplit(api, period, enabled = true) {
  const [split, setSplit] = useState(null);
  const reload = useCallback(async () => {
    if (!enabled || !period?.start || !period?.end) { setSplit(null); return null; }
    try {
      const r = await api.get('/portal/invoices2/company-split', { params: { start: period.start, end: period.end } });
      setSplit(r.data);
      return r.data;
    } catch { setSplit(null); return null; }
  }, [api, period?.start, period?.end, enabled]);
  useEffect(() => { reload(); }, [reload]);
  return { split, reload };
}

export const otherCompany = (split, company) => (split?.companies || []).find(c => c.company !== company && c.days > 0) || null;

export const createUrl = (c, period) => `/portal/invoice/create?company=${c}&start=${period.start}&end=${period.end}`;

/** Wizard note: days for the other company in this period are NOT on this invoice. */
export function OtherCompanyNote({ split, company }) {
  const other = otherCompany(split, company);
  if (!other) return null;
  return (
    <div className="text-xs text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-xl px-3 py-2.5 flex gap-2" data-testid="other-company-note">
      <Building2 className="w-4 h-4 shrink-0 mt-0.5 text-amber-300" />
      <div>
        <span className="font-semibold">{other.days} day{other.days === 1 ? '' : 's'} on {other.name} jobs</span> in this period aren't on this invoice — {COMPANY_NAME.LD} and {COMPANY_NAME.LC} are invoiced separately. We'll prompt you for the {other.name} invoice once this one is issued.
      </div>
    </div>
  );
}

/** "Still to invoice" chips — one per company with uninvoiced days. */
export function ToInvoiceChips({ split, onPick, title = 'Still to invoice' }) {
  const due = (split?.companies || []).filter(c => c.days > 0);
  if (due.length === 0) return null;
  return (
    <div className="rounded-xl bg-[#2A3240] border border-white/10 p-3 space-y-2" data-testid="to-invoice-card">
      <div className="text-xs text-gray-400">{title} · {ukDate(split.period_start)} – {ukDate(split.period_end)}{due.length > 1 ? ' · two invoices, one per company' : ''}</div>
      <div className="space-y-1.5">
        {due.map(c => (
          <button key={c.company} type="button" onClick={() => onPick(c)} className="w-full text-left rounded-lg border border-[#F5A623]/30 bg-[#F5A623]/10 px-3 py-2.5 flex items-center gap-3 active:scale-[0.99]" data-testid={`to-invoice-${c.company}`}>
            <Building2 className="w-4 h-4 text-[#F5A623] shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-white font-semibold text-sm">{c.name}</div>
              <div className="text-[11px] text-gray-400 truncate">{c.days} day{c.days === 1 ? '' : 's'} · {c.projects.map(p => p.project_code).join(', ')}</div>
            </div>
            <ArrowRight className="w-4 h-4 text-[#F5A623]" />
          </button>
        ))}
      </div>
    </div>
  );
}
